/**
 * Convert a licence date entered as DD/MM/YY into the API format YYYY-MM-DD.
 * Century rule (kept in sync with parseDisplayDate in the profile form):
 *   00-69 -> 2000-2069, 70-99 -> 1970-1999.
 */
const expandTwoDigitYear = (yy) => {
  const n = parseInt(yy, 10);
  return n <= 69 ? 2000 + n : 1900 + n;
};

const formatDateForAPI = (dateString) => {
  if (!dateString) return null;

  const parts = dateString.split('/');
  if (parts.length === 3) {
    const day = parts[0].padStart(2, '0');
    const month = parts[1].padStart(2, '0');
    // 2-digit year -> 4-digit; tolerate an already-4-digit year just in case.
    const year = parts[2].length === 2 ? expandTwoDigitYear(parts[2]) : parts[2];
    return `${year}-${month}-${day}`;
  }
  return dateString;
};

/**
 * Transforms institution application form data from UI format to database schema format
 * @param {Object} formData - The form data from the UI component
 * @returns {Object} - Transformed data matching database schema
 */
export const transformFormDataToAPI = (formData) => {
  try {
    // Main submission data (SefSubmission table)
    const submission = transformSubmissionData(formData);
    
    // Contact data (SefContact table)
    const contacts = transformContactData(formData);
    
    // Qualification data (new tables)
    const local_attendance = transformQualificationData(formData.localAttendance);
    const local_achievement = transformQualificationData(formData.localAchievement);
    const external_achievement = transformQualificationData(formData.externalAchievement);

    return {
      submission,
      contacts,
      local_attendance,
      local_achievement,
      external_achievement
    };
  } catch (error) {
    console.error('Error transforming form data:', error);
    throw new Error('Failed to transform form data for submission');
  }
};

/**
 * Transform main submission data
 */
const transformSubmissionData = (formData) => {
  return {
    institute_name_en: formData.instituteNameEn || null,
    institute_name_ar: formData.instituteNameAr || null,
    licensing_body: formData.licensingBody || null,
    year_of_establishment: formData.yearOfEstablishment ? parseInt(formData.yearOfEstablishment) : null,
    license_number: formData.licenseNumber || null,
    license_issue_date: formData.issueDate ? formatDateForAPI(formData.issueDate) : null,
    license_expiry_date: formData.expiryDate ? formatDateForAPI(formData.expiryDate) : null,
    institute_email: formData.instituteEmail || null,
    institute_website: formData.instituteWebsite || null,
    social_media: formData.socialMediaAccount ? [formData.socialMediaAccount] : null,
    institute_address: formData.instituteAddress || null,
    nqf_status: formData.nqfStatus || null,
    qualifications_on_nqf: JSON.stringify(formData.nqfQualifications),
    general_information: formData.generalInformation || null,
    main_characteristics: formData.mainCharacteristics || null,
    effectiveness_features: formData.effectivenessFeatures || null,
    service_employee_name_en: formData.serviceEmployeeNameEn || null,
    service_employee_name_ar: formData.serviceEmployeeNameAr || null,
    service_employee_cpr: formData.serviceEmployeeCpr || null,
    service_employee_position: formData.serviceEmployeePosition || null,
    service_employee_office: formData.serviceEmployeeOffice || null,
    service_employee_mobile: formData.serviceEmployeeMobile || null,
    service_employee_email: formData.serviceEmployeeEmail || null
  };
};

/**
 * Transform contact data
 */
const transformContactData = (formData) => {
  const contacts = [];
  
  // Owner contact
  if (formData.ownerName || formData.ownerEmail || formData.ownerMobile) {
    contacts.push({
      role: "Owner",
      name: formData.ownerName || null,
      mobile_no: formData.ownerMobile || null,
      email: formData.ownerEmail || null
    });
  }
  
  // CEO/Executive contact
  if (formData.executiveName || formData.executiveEmail || formData.executiveMobile) {
    contacts.push({
      role: "CEO",
      name: formData.executiveName || null,
      mobile_no: formData.executiveMobile || null,
      email: formData.executiveEmail || null
    });
  }
  
  // Nominee contact
  if (formData.nomineeName || formData.nomineeEmail || formData.nomineeMobile) {
    contacts.push({
      role: "Nominee",
      name: formData.nomineeName || null,
      mobile_no: formData.nomineeMobile || null,
      email: formData.nomineeEmail || null
    });
  }
  
  return contacts;
};

/**
 * Transform qualification data
 */
const transformQualificationData = (qualificationData) => {
  const records = [];
  
  // Create one record per year
  Object.keys(qualificationData).forEach(year => {
    const yearData = qualificationData[year];
    
    // Only create record if at least one field has data
    if (yearData.male || yearData.female) {
      const maleCount = yearData.male ? parseInt(yearData.male) : 0;
      const femaleCount = yearData.female ? parseInt(yearData.female) : 0;
      const totalCount = maleCount + femaleCount;
      
      records.push({
        year: parseInt(year),
        male_count: maleCount,
        female_count: femaleCount,
        total_count: totalCount
      });
    }
  });
  
  return records;
};